import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface Plan {
  id: string;
  code: string;
  label: string;
  description: string | null;
  monthlyPrice: number | string;
  yearlyPrice: number | string | null;
  monthlyListingsQuota: number;
  featuredListingsQuota: number;
  commissionShare: number | string;
  features: string[] | null;
  highlight: boolean;
}

interface PaymentMethod {
  channel: string;
  gateway: string | null;
  label: string;
  icon: string | null;
  description: string | null;
  feePercent: number;
  feeFixed: number;
  fee: number;
  total: number | null;
}

interface CheckoutResult {
  payment: {
    id: string;
    reference: string;
    total: number | string;
    status: string;
    gatewayUrl: string | null;
  };
  invoice: { id: string; invoiceNumber: string };
  bankInstructions: null | {
    iban: string;
    bank: string;
    beneficiary: string;
    reference: string;
  };
}

export function SubscriptionPage() {
  const [searchParams] = useSearchParams();
  const requestedPlanCode = searchParams.get('plan');
  const requestedCycle = (searchParams.get('cycle') as 'monthly' | 'yearly' | null) ?? 'monthly';

  const [cycle, setCycle] = useState<'monthly' | 'yearly'>(requestedCycle);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => api.get<ApiSuccess<Plan[]>>('/api/subscription-plans'),
  });

  const plans = plansData?.data ?? [];

  // 🚀 لو الباقة محدّدة في الـ URL، افتح الـ checkout مباشرة
  useEffect(() => {
    if (!requestedPlanCode || selectedPlan) return;
    const found = plans.find((p) => p.code === requestedPlanCode);
    if (found) setSelectedPlan(found);
  }, [requestedPlanCode, plans, selectedPlan]);

  // 🎯 لو جا من /brokers#plans مع كود محدّد — لا نعرض شبكة الباقات، نعرض الـ checkout فقط
  if (requestedPlanCode && selectedPlan) {
    return (
      <div>
        <div className="mb-4">
          <Link to="/brokers#plans" className="text-sm text-gold font-bold hover:underline">
            ← تغيير الباقة
          </Link>
        </div>
        <CheckoutModal
          plan={selectedPlan}
          cycle={cycle}
          onClose={() => window.location.href = '/brokers#plans'}
        />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-2">💎 اشتراك المسوّق</h1>
      <p className="text-gray-600 mb-6">اختر الخطة الأنسب لنشاطك العقاري</p>

      <div className="flex justify-center mb-6">
        <div className="bg-gray-100 rounded-xl p-1 inline-flex">
          <button
            onClick={() => setCycle('monthly')}
            className={`px-6 py-2 rounded-lg font-bold text-sm transition ${cycle === 'monthly' ? 'bg-navy-dark text-white' : 'text-gray-600'}`}
          >شهري</button>
          <button
            onClick={() => setCycle('yearly')}
            className={`px-6 py-2 rounded-lg font-bold text-sm transition ${cycle === 'yearly' ? 'bg-navy-dark text-white' : 'text-gray-600'}`}
          >
            سنوي <Badge color="green">وفّر شهرين</Badge>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const price = cycle === 'yearly' ? Number(p.yearlyPrice ?? Number(p.monthlyPrice) * 12) : Number(p.monthlyPrice);
          return (
            <Card key={p.id} className={p.highlight ? 'border-2 border-gold relative' : ''}>
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy-dark text-xs font-black px-3 py-1 rounded-full">
                  ⭐ الأكثر طلباً
                </div>
              )}
              <CardBody>
                <div className="text-2xl font-black text-navy-dark">{p.label}</div>
                <p className="text-sm text-gray-500 mb-3">{p.description}</p>
                <div className="text-3xl font-black text-navy-dark">
                  {price.toLocaleString('en-US')}
                  <span className="text-sm font-normal text-gray-500"> ر.س/{cycle === 'yearly' ? 'سنة' : 'شهر'}</span>
                </div>
                <ul className="mt-4 space-y-1 text-sm">
                  {(p.features ?? []).map((f, i) => <li key={i}>✓ {f}</li>)}
                </ul>
                <Button
                  variant={p.highlight ? 'primary' : 'outline'}
                  className="w-full mt-4"
                  onClick={() => setSelectedPlan(p)}
                  disabled={price === 0}
                >
                  {price === 0 ? 'الخطة الافتراضية' : 'اشترك'}
                </Button>
              </CardBody>
            </Card>
          );
        })}
      </div>

      {selectedPlan && (
        <CheckoutModal
          plan={selectedPlan}
          cycle={cycle}
          onClose={() => setSelectedPlan(null)}
        />
      )}
    </div>
  );
}

function CheckoutModal({ plan, cycle, onClose }: { plan: Plan; cycle: 'monthly' | 'yearly'; onClose: () => void }) {
  const qc = useQueryClient();
  const basePrice = cycle === 'yearly' ? Number(plan.yearlyPrice ?? Number(plan.monthlyPrice) * 12) : Number(plan.monthlyPrice);

  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{ code: string; discount: number; finalAmount: number } | null>(null);
  const [discountErr, setDiscountErr] = useState('');
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [result, setResult] = useState<CheckoutResult | null>(null);

  const finalAmount = appliedDiscount ? appliedDiscount.finalAmount : basePrice;

  const { data: methodsData } = useQuery({
    queryKey: ['payment-methods', 'BROKER_SUBSCRIPTION', finalAmount],
    queryFn: () => api.get<ApiSuccess<PaymentMethod[]>>('/api/payment-methods/available', { query: { purpose: 'BROKER_SUBSCRIPTION', amount: finalAmount } }),
  });

  const validateDiscount = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ discount: number; finalAmount: number; code: string }>>('/api/admin/discount-codes/validate', {
      code: discountCode,
      target: 'BROKER_SUBSCRIPTION',
      amount: basePrice,
    }),
    onSuccess: (r) => { setAppliedDiscount(r.data); setDiscountErr(''); },
    onError: (e: Error) => { setDiscountErr(e.message); setAppliedDiscount(null); },
  });

  const checkout = useMutation({
    mutationFn: () => api.post<ApiSuccess<CheckoutResult>>('/api/payments/subscription/checkout', {
      planId: plan.id,
      cycle,
      channel: selectedChannel,
      discountCode: appliedDiscount?.code,
    }),
    onSuccess: (r) => { setResult(r.data); qc.invalidateQueries({ queryKey: ['me'] }); },
  });

  // إذا تمت العملية وفيه رابط بوابة → اذهب
  if (result?.payment.gatewayUrl) {
    window.location.href = result.payment.gatewayUrl;
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">💎 الاشتراك في {plan.label}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        {result ? (
          <SuccessOrPending result={result} onClose={onClose} />
        ) : (
          <div className="p-5 space-y-5">
            {/* الملخص */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">الخطة</span>
                <span className="font-bold">{plan.label} ({cycle === 'yearly' ? 'سنوي' : 'شهري'})</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">السعر الأساسي</span>
                <span className="font-bold">{basePrice.toLocaleString('en-US')} ر.س</span>
              </div>
              {appliedDiscount && (
                <div className="flex justify-between text-sm mb-1 text-green-700">
                  <span>خصم ({appliedDiscount.code})</span>
                  <span className="font-bold">-{appliedDiscount.discount.toLocaleString('en-US')} ر.س</span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-200 pt-2 mt-2 text-lg font-black">
                <span>الإجمالي</span>
                <span className="text-navy-dark">{finalAmount.toLocaleString('en-US')} ر.س</span>
              </div>
            </div>

            {/* كود خصم */}
            <fieldset className="border border-gray-200 rounded-xl p-3">
              <legend className="px-2 text-sm font-bold text-navy-dark">🎫 كود خصم (اختياري)</legend>
              <div className="flex gap-2">
                <Input value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder="SUMMER25" dir="ltr" />
                <Button variant="ghost" onClick={() => validateDiscount.mutate()} loading={validateDiscount.isPending} disabled={!discountCode}>تطبيق</Button>
              </div>
              {discountErr && <div className="text-xs text-red-600 mt-2">⛔ {discountErr}</div>}
              {appliedDiscount && <div className="text-xs text-green-600 mt-2">✓ تم تطبيق الكود</div>}
            </fieldset>

            {/* وسائل الدفع */}
            <fieldset className="border border-gray-200 rounded-xl p-3">
              <legend className="px-2 text-sm font-bold text-navy-dark">💳 وسيلة الدفع</legend>
              {!methodsData?.data?.length ? (
                <div className="text-sm text-gray-500 text-center py-3">لا توجد وسائل دفع متاحة</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {methodsData.data.map((m) => {
                    const active = selectedChannel === m.channel;
                    return (
                      <button
                        key={m.channel}
                        onClick={() => setSelectedChannel(m.channel)}
                        className={`text-right p-3 rounded-xl border-2 transition ${
                          active ? 'border-navy bg-navy/5' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{m.icon || '💳'}</div>
                          <div className="flex-1">
                            <div className="font-bold text-sm">{m.label}</div>
                            <div className="text-[10px] text-gray-500">
                              {m.fee > 0 ? `+ ${m.fee.toLocaleString('en-US')} ر.س رسوم` : 'بدون رسوم'}
                            </div>
                          </div>
                          {active && <span className="text-navy">●</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </fieldset>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" onClick={onClose}>إلغاء</Button>
              <Button
                variant="primary"
                onClick={() => checkout.mutate()}
                loading={checkout.isPending}
                disabled={!selectedChannel}
              >
                💎 إتمام الاشتراك
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SuccessOrPending({ result, onClose }: { result: CheckoutResult; onClose: () => void }) {
  // تحويل بنكي
  if (result.bankInstructions) {
    return (
      <div className="p-5 space-y-4">
        <div className="text-center text-5xl">🏦</div>
        <h3 className="text-center text-xl font-black text-navy-dark">حوّل المبلغ ثم ارفع الإيصال</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2 text-sm" dir="ltr">
          <div className="flex justify-between"><span className="text-gray-600">البنك:</span><b>{result.bankInstructions.bank}</b></div>
          <div className="flex justify-between"><span className="text-gray-600">المستفيد:</span><b>{result.bankInstructions.beneficiary}</b></div>
          <div className="flex justify-between"><span className="text-gray-600">IBAN:</span><b className="font-mono">{result.bankInstructions.iban}</b></div>
          <div className="flex justify-between"><span className="text-gray-600">المرجع:</span><b className="font-mono">{result.bankInstructions.reference}</b></div>
          <div className="flex justify-between border-t pt-2"><span className="text-gray-600">المبلغ:</span><b className="text-lg">{Number(result.payment.total).toLocaleString('en-US')} ر.س</b></div>
        </div>
        <p className="text-xs text-gray-500 text-center">
          بعد التحويل، ارفع صورة الإيصال من <b>المحفظة → معاملاتي</b> وستراجعها الإدارة خلال 24 ساعة.
        </p>
        <Button variant="primary" className="w-full" onClick={onClose}>تم</Button>
      </div>
    );
  }

  // محفظة (نجح فوراً)
  if (result.payment.status === 'SUCCEEDED') {
    return (
      <div className="p-5 text-center space-y-4">
        <div className="text-6xl">✅</div>
        <h3 className="text-xl font-black text-green-700">تم الاشتراك بنجاح!</h3>
        <p className="text-sm text-gray-600">رقم الفاتورة: <b dir="ltr">{result.invoice.invoiceNumber}</b></p>
        <Button variant="primary" className="w-full" onClick={onClose}>متابعة</Button>
      </div>
    );
  }

  // معلّق
  return (
    <div className="p-5 text-center space-y-4">
      <div className="text-6xl">⏳</div>
      <h3 className="text-xl font-black text-navy-dark">الفاتورة منشأة</h3>
      <p className="text-sm text-gray-600">المرجع: <b dir="ltr">{result.payment.reference}</b></p>
      <Button variant="primary" className="w-full" onClick={onClose}>تم</Button>
    </div>
  );
}
